import { useEffect, useMemo, useState } from "react";
import api, { fmtTL, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { TrendingUp, TrendingDown, Store, FileSpreadsheet, AlertCircle, AlertTriangle, Scale, HeartPulse, Utensils, ShoppingBag, Phone, PieChart as PieChartIcon, Package, Wifi, Percent, Download, ExternalLink, CreditCard } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

// helpers
const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

// Chart config — extracted to module scope so React doesn't recreate on every render
const CHART_AXIS_TICK = { fontSize: 11, fill: "#8A8A8A" };
const CHART_TOOLTIP_STYLE = { background: "#fff", border: "1px solid #DED9CE", borderRadius: 8 };
const CHART_GRID_STROKE = "#EAE6DF";

// Department icons/badges — shared with ReportForm styling
const DEPT_META = [
  { key: "dept_masa", label: "Masa", bg: "#8B5E3C", letter: "M" },
  { key: "dept_algotur", label: "Al-Götür", bg: "#4A7C59", letter: "A" },
  { key: "dept_telefon", label: "Telefon Paket", bg: "#2A6F97", letter: "T" },
  { key: "dept_trendyol", label: "Trendyol", bg: "#F27A1A", letter: "T" },
  { key: "dept_yemek_sepeti", label: "Yemek Sepeti", bg: "#FA0037", letter: "Y" },
  { key: "dept_getir", label: "Getir Yemek", bg: "#5D3EBC", letter: "G" },
  { key: "dept_migros", label: "Migros", bg: "#FDB300", letter: "M", fg: "#3B2600" },
];

const DeptIcon = ({ dept }) => {
  if (dept === "dept_masa") return <Utensils className="h-4 w-4 text-[#8B5E3C]" aria-hidden="true" />;
  if (dept === "dept_algotur") return <ShoppingBag className="h-4 w-4 text-[#4A7C59]" aria-hidden="true" />;
  if (dept === "dept_telefon") return <Phone className="h-4 w-4 text-[#2A6F97]" aria-hidden="true" />;
  const m = DEPT_META.find((d) => d.key === dept);
  if (!m) return null;
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-bold shrink-0"
      style={{ background: m.bg, color: m.fg || "#fff" }}
    >
      {m.letter}
    </span>
  );
};

function DeltaChip({ delta }) {
  if (!delta || !Number.isFinite(delta.pct)) return null;
  const pos = delta.pct > 0;
  const zero = delta.pct === 0;
  const cls = zero
    ? "text-slate-500 bg-slate-100"
    : pos
      ? "text-[#4A7C59] bg-[#EFF5EC]"
      : "text-[#C84B31] bg-[#FDF0ED]";
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      <span>{pos ? "▲" : zero ? "◆" : "▼"}</span>
      <span>%{Math.abs(delta.pct).toFixed(1)}</span>
    </span>
  );
}

function KPI({ label, value, icon: Icon, tone = "default", testId, delta = null }) {
  const toneMap = {
    default: "text-slate-900",
    positive: "text-[#4A7C59]",
    negative: "text-[#C84B31]",
    warning: "text-[#F2A65A]",
  };
  // delta.pct: pozitif = artış, negatif = düşüş. For "gider" gibi negatif kalemler yorumlama farklı;
  // burada sadece işaret gösteriyoruz, renk tone'a bırakılıyor.
  const showDelta = delta && Number.isFinite(delta.pct);
  const deltaColor = showDelta
    ? delta.pct > 0
      ? "text-[#4A7C59] bg-[#EFF5EC]"
      : delta.pct < 0
      ? "text-[#C84B31] bg-[#FDF0ED]"
      : "text-slate-500 bg-slate-100"
    : "";
  return (
    <div className="k-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className={`font-mono text-2xl font-semibold tracking-tight ${toneMap[tone]}`}>{value}</div>
      {showDelta && (
        <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${deltaColor}`}>
          <span>{delta.pct > 0 ? "▲" : delta.pct < 0 ? "▼" : "◆"}</span>
          <span>%{Math.abs(delta.pct).toFixed(1)}</span>
          <span className="text-slate-400 font-normal">geçen hafta</span>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dayCompare, setDayCompare] = useState(null);
  const [health, setHealth] = useState([]);
  const [branchPerf, setBranchPerf] = useState(null);  // kg döner cirosu için
  const [monthlyPerBranch, setMonthlyPerBranch] = useState([]); // Şubelere göre ciro — bulunduğumuz ay
  const [deptScope, setDeptScope] = useState("all");
  const [deptDate, setDeptDate] = useState(yesterdayISO());
  const [deptDaily, setDeptDaily] = useState(null);
  const [trendMode, setTrendMode] = useState("total");
  const [branches, setBranches] = useState([]);
  const [dashboardBranch, setDashboardBranch] = useState("all"); // Üst özet + KPI kartları için şube filtresi
  const [recent, setRecent] = useState([]);
  const [recentBranch, setRecentBranch] = useState("all");
  const [recentFrom, setRecentFrom] = useState("");
  const [recentTo, setRecentTo] = useState("");
  // Bu Ay Anomaliler widget'ı (yönetici görür) — son 3 ay dropdown seçimi
  const [anomalies, setAnomalies] = useState(null);
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
      const label = d.toLocaleDateString("tr-TR", { year: "numeric", month: "long" });
      opts.push({ value: val, label });
    }
    return opts;
  }, []);
  const [anomaliesMonth, setAnomaliesMonth] = useState(monthOptions[0].value);
  // Bu haftanın yemek kartı net alacağı — Panel widget'ı
  const [mcWeek, setMcWeek] = useState(null);
  const [brandSummary, setBrandSummary] = useState(null);
  // Online Panel duplicate warning (aynı platform ID birden fazla satırda)
  const [opDuplicates, setOpDuplicates] = useState({ conflicts: [], total: 0 });
  const [opDupDismissed, setOpDupDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kh_op_dup_dismissed") || "[]"); } catch { return []; }
  });

  const isAdmin = user?.role === "admin";
  const isRegional = user?.role === "regional_manager";
  const isManager = isAdmin || isRegional;

  useEffect(() => {
    const statsParams = { days: 30 };
    const compareParams = {};
    if (isManager && dashboardBranch !== "all") {
      statsParams.branch_id = dashboardBranch;
      compareParams.branch_id = dashboardBranch;
    }
    api.get("/dashboard/stats", { params: statsParams }).then((r) => setStats(r.data));
    // Şubelere Göre Ciro chart'ı — mevcut ay bazlı
    api.get("/dashboard/per-branch-monthly").then((r) => setMonthlyPerBranch(r.data?.per_branch || [])).catch(() => setMonthlyPerBranch([]));
    api.get("/dashboard/day-compare", { params: compareParams }).then((r) => setDayCompare(r.data));
    // Marka bazlı ciro — sadece Yönetici/Bölge Müdürü; şubeler kendi marka kırılımını görmez
    if (isManager) {
      api.get("/dashboard/brand-summary", { params: compareParams }).then((r) => setBrandSummary(r.data)).catch(() => {});
    }
    if (isManager) {
      api.get("/branches/health?days=30").then((r) => setHealth(r.data.branches || []));
      api.get("/branches").then((r) => setBranches(r.data || []));
      // Online Panel duplicate uyarısı (aynı platform ID birden çok satırda)
      api.get("/online-panel/duplicates").then((r) => setOpDuplicates(r.data || { conflicts: [], total: 0 })).catch(() => {});
      // kg Döner Cirosu — bu ay için şube karşılaştırması
      const _now = new Date();
      const _thisMonth = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}`;
      api.get("/analytics/branch-performance", { params: { month: _thisMonth } })
        .then((r) => setBranchPerf(r.data))
        .catch(() => setBranchPerf(null));
      // Bu Ay Anomaliler — seçili ay değiştikçe yeniden yükle
      api.get("/analytics/anomalies", { params: { month: anomaliesMonth } })
        .then((r) => setAnomalies(r.data))
        .catch(() => setAnomalies(null));
    }
  }, [isManager, dashboardBranch, anomaliesMonth]);

  useEffect(() => {
    const params = { limit: 20 };
    if (isManager && recentBranch !== "all") params.branch_id = recentBranch;
    if (recentFrom) params.date_from = recentFrom;
    if (recentTo) params.date_to = recentTo;
    api.get("/reports", { params }).then((r) => setRecent(r.data || []));
  }, [isManager, recentBranch, recentFrom, recentTo]);

  useEffect(() => {
    api.get(`/dashboard/dept-daily?date=${deptDate}`).then((r) => setDeptDaily(r.data));
  }, [deptDate]);

  // Bu haftanın yemek kartı net alacağı: bu ay+geçen ay verilerini birleştir,
  // ödenmemiş & payment_date bu haftanın Pzt-Pzr aralığında olanları hesapla.
  useEffect(() => {
    if (!isManager) return;
    const now = new Date();
    const dow = (now.getDay() + 6) % 7; // Pzt=0
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - dow);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
    const iso = (d) => d.toISOString().slice(0, 10);
    const thisMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
    // Haftanın hem bu ayda hem geçen ayda kayıtları olabilir; iki ay çek.
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonth = `${prev.getFullYear()}-${(prev.getMonth() + 1).toString().padStart(2, "0")}`;
    Promise.all([
      api.get("/mc-payments", { params: { month: thisMonth } }).catch(() => ({ data: { providers: {} } })),
      api.get("/mc-payments", { params: { month: prevMonth } }).catch(() => ({ data: { providers: {} } })),
    ]).then(([r1, r2]) => {
      const wStart = iso(weekStart), wEnd = iso(weekEnd);
      const items = [];
      let net = 0, amount = 0;
      const byProvider = {};
      for (const src of [r1.data, r2.data]) {
        for (const [prov, rows] of Object.entries(src.providers || {})) {
          for (const row of rows) {
            if (row.paid) continue;
            if (!row.payment_date) continue;
            if (row.payment_date < wStart || row.payment_date > wEnd) continue;
            const a = Number(row.amount || 0);
            const c = Number(row.commission || 0);
            items.push({ ...row, provider: prov });
            net += a - c;
            amount += a;
            byProvider[prov] = (byProvider[prov] || 0) + (a - c);
          }
        }
      }
      setMcWeek({ items, net, amount, byProvider, weekStart: wStart, weekEnd: wEnd });
    });
  }, [isManager]);

  const deptRows = useMemo(() => {
    if (!deptDaily) return [];
    const source =
      deptScope === "all"
        ? deptDaily.dept_totals
        : (deptDaily.dept_by_branch || []).find((b) => b.branch_id === deptScope)?.depts;
    if (!source) return [];
    return DEPT_META.map((m) => {
      const rev = source[m.key]?.revenue || 0;
      const disc = source[m.key]?.discount || 0;
      const orders = source[m.key]?.orders || 0;
      const gross = rev + disc;
      const rate = gross > 0 ? (disc / gross) * 100 : 0;
      return { ...m, rev, disc, orders, rate };
    });
  }, [deptDaily, deptScope]);
  const deptTotals = useMemo(() => {
    return deptRows.reduce(
      (acc, r) => {
        acc.rev += r.rev;
        acc.disc += r.disc;
        acc.orders += r.orders;
        return acc;
      },
      { rev: 0, disc: 0, orders: 0 }
    );
  }, [deptRows]);
  const deptTotalRate = deptTotals.rev + deptTotals.disc > 0 ? (deptTotals.disc / (deptTotals.rev + deptTotals.disc)) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 sm:flex-1">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            {isAdmin ? "Yönetici Paneli" : isRegional ? "Bölge Paneli" : "Şube Paneli"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Dünkü özet · geçen haftanın aynı günü ile karşılaştırma
            {dayCompare?.date && (
              <span className="ml-2 text-slate-400">
                ({dayCompare.date}
                {dayCompare.compare_date && <> · vs <span className="font-mono">{dayCompare.compare_date}</span></>}
                )
              </span>
            )}
            {isManager && dashboardBranch !== "all" && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5F0FA] text-[#6A4A8A] text-[11px] font-semibold">
                {branches.find((b) => b.id === dashboardBranch)?.name || "Şube"}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap shrink-0">
          {isManager && branches.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Şube</span>
              <Select value={dashboardBranch} onValueChange={setDashboardBranch}>
                <SelectTrigger className="h-9 w-44 bg-white" data-testid="dashboard-branch-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Şubeler</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="text-xs text-slate-500 font-mono">
            Hoş geldin, <span className="text-slate-900 font-semibold">{user?.name || user?.username}</span>
          </div>
        </div>
      </header>

      {/* Online Panel Duplicate Uyarısı — aynı platform ID birden çok satırda */}
      {isManager && opDuplicates.conflicts.filter((c) => !opDupDismissed.includes(`${c.platform}:${c.platform_id}`)).length > 0 && (
        <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4" data-testid="op-duplicates-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-rose-900">
                Aynı platform ID'si birden çok Online Panel satırında kullanılıyor
              </div>
              <p className="text-sm text-rose-800 mt-0.5">
                Bu çakışmalar Trendyol Şube Eşleştirme'de yinelenen kayıtlara neden oluyor. Düzeltmek için Online Panel Verileri'ni açıp fazlalık satırları silin veya birleştirin.
              </p>
              <div className="mt-3 space-y-1.5">
                {opDuplicates.conflicts.filter((c) => !opDupDismissed.includes(`${c.platform}:${c.platform_id}`)).slice(0, 5).map((c) => (
                  <div key={`${c.platform}:${c.platform_id}`} className="flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-200/70 text-rose-900 text-[11px] font-semibold">
                      {c.platform_label}
                    </span>
                    <span className="font-mono text-rose-900">#{c.platform_id}</span>
                    <span className="text-rose-700">— {c.count} satırda:</span>
                    <span className="text-rose-800 truncate">
                      {c.rows.slice(0, 4).map((r) => `${r.marka || "-"} · ${r.sube || "-"}`).join(" | ")}
                      {c.rows.length > 4 && ` +${c.rows.length - 4}`}
                    </span>
                    <button
                      onClick={() => {
                        const key = `${c.platform}:${c.platform_id}`;
                        const nx = [...opDupDismissed, key];
                        setOpDupDismissed(nx);
                        localStorage.setItem("kh_op_dup_dismissed", JSON.stringify(nx));
                      }}
                      className="ml-auto text-[11px] text-rose-500 hover:text-rose-800 underline"
                      data-testid={`op-dup-dismiss-${c.platform}-${c.platform_id}`}
                    >
                      Gizle
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Link to="/online-panel" className="text-sm font-semibold text-rose-700 hover:text-rose-900 underline" data-testid="op-duplicates-open">
                  Online Panel Verileri'ni Aç →
                </Link>
                <button
                  onClick={() => {
                    const all = opDuplicates.conflicts.map((c) => `${c.platform}:${c.platform_id}`);
                    setOpDupDismissed(all);
                    localStorage.setItem("kh_op_dup_dismissed", JSON.stringify(all));
                  }}
                  className="text-xs text-rose-500 hover:text-rose-800 underline ml-auto"
                  data-testid="op-duplicates-dismiss-all"
                >
                  Tümünü gizle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPI label="Dünkü Ciro" value={fmtTL(dayCompare?.today?.turnover)} icon={TrendingUp} tone="positive" testId="kpi-turnover" delta={dayCompare?.delta?.turnover} />
        <KPI label="Dünkü Gider" value={fmtTL(dayCompare?.today?.expense)} icon={TrendingDown} tone="negative" testId="kpi-expense" delta={dayCompare?.delta?.expense} />
        <KPI label={isManager ? "Aktif Şube" : "Dünkü Rapor"} value={isManager ? (stats?.branch_count || 0) : (dayCompare?.today?.report_count || 0)} icon={isManager ? Store : FileSpreadsheet} testId="kpi-side" delta={isManager ? null : dayCompare?.delta?.report_count} />
      </div>

      {/* Satış Dağılımı — Dün · geçen hafta aynı gün karşılaştırması */}
      {dayCompare?.today?.sales_split && (() => {
        const ss = dayCompare.today.sales_split;
        const d = dayCompare.delta || {};
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sales-split">
            <div className="k-card p-5">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <PieChartIcon className="h-3.5 w-3.5" /> Dünkü Departman Cirosu
              </div>
              <div className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums" data-testid="ss-total">
                {fmtTL(ss.total)}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                <DeltaChip delta={d.sales_total} /> <span>geçen hafta</span>
                {(dayCompare.today.order_count || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#F3F1EC] text-slate-600 font-mono text-[10px]">
                    <ShoppingBag className="h-3 w-3" /> {dayCompare.today.order_count} sipariş
                  </span>
                )}
              </div>
            </div>
            <div className="k-card p-5">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Utensils className="h-3.5 w-3.5" /> İçeri Satış <span className="text-slate-400">(Masa + Al-Götür)</span>
              </div>
              <div className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums" data-testid="ss-in-store">
                {fmtTL(ss.in_store.amount)}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                <span>%{ss.in_store.pct}</span><DeltaChip delta={d.in_store} />
                {(ss.in_store.orders || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#F3F1EC] text-slate-600 font-mono text-[10px]" data-testid="ss-in-store-orders">
                    <ShoppingBag className="h-3 w-3" /> {ss.in_store.orders} sipariş
                  </span>
                )}
              </div>
            </div>
            <div className="k-card p-5">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Package className="h-3.5 w-3.5" /> Paket Satış <span className="text-slate-400">(Telefon + Online)</span>
              </div>
              <div className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums" data-testid="ss-package">
                {fmtTL(ss.package.amount)}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                <span>%{ss.package.pct}</span><DeltaChip delta={d.package} />
                {(ss.package.orders || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#F3F1EC] text-slate-600 font-mono text-[10px]" data-testid="ss-package-orders">
                    <ShoppingBag className="h-3 w-3" /> {ss.package.orders} sipariş
                  </span>
                )}
              </div>
            </div>
            <div className="k-card p-5 bg-[#FFF6F1] border-[#F5D3C0]">
              <div className="flex items-center gap-2 text-xs text-[#B8451F]">
                <Wifi className="h-3.5 w-3.5" /> Online (TY+YS+Getir+Migros)
              </div>
              <div className="mt-2 font-display text-2xl font-semibold text-[#D95D39] tabular-nums" data-testid="ss-online">
                {fmtTL(ss.online.amount)}
              </div>
              <div className="mt-2 text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
                <span>%{ss.online.pct}</span>
                <DeltaChip delta={d.online} />
                {(ss.online.orders || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/70 text-[#B8451F] font-mono text-[10px]" data-testid="ss-online-orders">
                    <ShoppingBag className="h-3 w-3" /> {ss.online.orders} sipariş
                  </span>
                )}
                <span className="inline-flex items-center gap-1"><Percent className="h-3 w-3" /> İnd: {fmtTL(ss.online.discount)} (%{ss.online.discount_pct})</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Marka Bazlı Ciro kaldırıldı — kullanıcı isteği (2026-02-02) */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`k-card p-5 ${isManager ? "lg:col-span-3" : "lg:col-span-2"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-medium text-slate-900">Günlük Ciro Trendi</h3>
            <div className="inline-flex rounded-lg bg-[#F3F1EC] p-1 text-xs" data-testid="trend-mode-toggle">
              <button
                type="button"
                data-testid="trend-mode-total"
                onClick={() => setTrendMode("total")}
                className={`px-3 py-1.5 rounded-md transition-colors ${trendMode === "total" ? "bg-white text-slate-900 shadow-sm font-semibold" : "text-slate-500 hover:text-slate-700"}`}
              >Toplam</button>
              <button
                type="button"
                data-testid="trend-mode-depts"
                onClick={() => setTrendMode("depts")}
                className={`px-3 py-1.5 rounded-md transition-colors ${trendMode === "depts" ? "bg-white text-slate-900 shadow-sm font-semibold" : "text-slate-500 hover:text-slate-700"}`}
              >Departman</button>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                <XAxis dataKey="date" tick={CHART_AXIS_TICK} />
                <YAxis tick={CHART_AXIS_TICK} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => fmtTL(v)} />
                {trendMode === "total" ? (
                  <Line type="monotone" dataKey="turnover" name="Ciro" stroke="#D95D39" strokeWidth={2} dot={false} />
                ) : (
                  DEPT_META.map((m) => (
                    <Line key={m.key} type="monotone" dataKey={m.key} name={m.label} stroke={m.bg} strokeWidth={2} dot={false} />
                  ))
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {trendMode === "depts" && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px]">
              {DEPT_META.map((m) => (
                <div key={m.key} className="flex items-center gap-1.5 text-slate-600">
                  <span className="inline-block w-3 h-0.5" style={{ background: m.bg }} />
                  <span>{m.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {!isManager && (
          <div className="k-card p-5">
            <h3 className="font-display font-medium text-slate-900 mb-4">Hızlı işlem</h3>
            <Link
              to="/report/new"
              className="block w-full text-center px-4 py-3 rounded-lg bg-[#D95D39] text-white font-medium hover:bg-[#C24F2C] transition-colors"
            >
              Bugünkü Kasayı Doldur
            </Link>
          </div>
        )}
      </div>

      {/* Bu Haftanın Yemek Kartı Net Alacağı — hızlı bakış */}
      {isManager && mcWeek && mcWeek.items.length > 0 && (
        <div className="k-card p-5" data-testid="mc-week-widget">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#D95D39]" />
              <h3 className="font-display font-medium text-slate-900">Bu Hafta Yemek Kartı Alacağı</h3>
              <span className="text-[11px] text-slate-500 font-mono">
                ({(() => { const [y,m,d]=mcWeek.weekStart.split("-"); return `${d}.${m}`; })()} → {(() => { const [y,m,d]=mcWeek.weekEnd.split("-"); return `${d}.${m}`; })()})
              </span>
              <span className="text-xs text-slate-500">— {mcWeek.items.length} kayıt</span>
            </div>
            <Link
              to="/meal-card-payments"
              className="text-[11px] font-medium text-[#78350F] hover:underline inline-flex items-center gap-0.5"
              data-testid="mc-week-goto"
            >
              Detay <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg bg-[#FFF8E9] border border-[#E9C47A]/50 p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#8A6A24]">Net Alacak</div>
              <div className="mt-1 font-display text-2xl font-semibold text-[#7A5A15] tabular-nums">{fmtTL(mcWeek.net)}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Tutar: <span className="font-mono text-slate-700">{fmtTL(mcWeek.amount)}</span></div>
            </div>
            {["multinet", "sodexo", "ticket", "setcard"].map((p) => {
              const v = mcWeek.byProvider[p] || 0;
              if (v === 0) return null;
              const colors = { multinet: "#4A6FA5", sodexo: "#8A3418", ticket: "#4A7C59", setcard: "#B8451F" };
              return (
                <div key={p} className="rounded-lg bg-white border border-[#EAE6DF] p-3" data-testid={`mc-week-${p}`}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: colors[p] }}>{p}</div>
                  <div className="mt-1 font-display text-lg font-semibold text-slate-900 tabular-nums">{fmtTL(v)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bu Ay Anomaliler — Kasa farkı & zincir kırıkları özeti */}
      {isManager && anomalies && (
        <div className="k-card p-5" data-testid="anomalies-widget">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="font-display font-medium text-slate-900 flex items-center gap-2">
              <AlertCircle className={`h-4 w-4 ${anomalies.total_anomalies > 0 ? "text-[#D95D39]" : "text-[#4A7C59]"}`} />
              Anomaliler
              <select
                value={anomaliesMonth}
                onChange={(e) => setAnomaliesMonth(e.target.value)}
                className="text-xs font-normal text-slate-700 bg-white border border-[#EAE6DF] rounded-md px-2 py-1 ml-2 cursor-pointer hover:border-[#D95D39] focus:outline-none focus:ring-2 focus:ring-[#D95D39]/30"
                data-testid="anomalies-month-select"
              >
                {monthOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </h3>
            <div className="text-xs text-slate-600 flex items-center gap-3">
              <span>Şube: <span className="font-semibold text-slate-800">{anomalies.total_branches}</span></span>
              <span>Sorunlu: <span className={`font-semibold ${anomalies.branches_with_issues > 0 ? "text-[#D95D39]" : "text-[#4A7C59]"}`}>{anomalies.branches_with_issues}</span></span>
              <span>Toplam Anomali: <span className={`font-mono font-bold ${anomalies.total_anomalies > 0 ? "text-[#D95D39]" : "text-[#4A7C59]"}`}>{anomalies.total_anomalies}</span></span>
            </div>
          </div>
          {anomalies.branches.filter((b) => b.total_anomalies > 0).length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              <span className="inline-flex items-center gap-1.5 text-[#4A7C59]">
                <span className="h-2 w-2 rounded-full bg-[#4A7C59]" />
                Tüm şubeler bu ay için temiz — anomali yok
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="anomalies-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#EAE6DF]">
                    <th className="py-2 pr-3">Şube</th>
                    <th className="py-2 px-3 text-right">Rapor</th>
                    <th className="py-2 px-3 text-right">Kasa Farkı</th>
                    <th className="py-2 px-3 text-right">Kasa Zinciri</th>
                    <th className="py-2 px-3 text-right">Merkez Zinciri</th>
                    <th className="py-2 px-3 text-right">Ürün Stoku</th>
                    <th className="py-2 px-3 text-right">Toplam</th>
                    <th className="py-2 pl-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {anomalies.branches.map((b) => {
                    const clean = b.total_anomalies === 0;
                    return (
                      <tr key={b.branch_id} className={`border-b border-[#F3EFE8] ${clean ? "" : "bg-[#FDF4EC]/50"}`} data-testid={`anomaly-row-${b.branch_id}`}>
                        <td className="py-2 pr-3 font-medium text-slate-800">{b.branch_name}</td>
                        <td className="py-2 px-3 text-right text-slate-600 font-mono">{b.report_count}</td>
                        <td className={`py-2 px-3 text-right font-mono ${b.discrepancy_count > 0 ? "text-[#D95D39] font-semibold" : "text-slate-400"}`}>{b.discrepancy_count > 0 ? b.discrepancy_count : "—"}</td>
                        <td className={`py-2 px-3 text-right font-mono ${b.cash_chain_break_count > 0 ? "text-[#D95D39] font-semibold" : "text-slate-400"}`}>{b.cash_chain_break_count > 0 ? b.cash_chain_break_count : "—"}</td>
                        <td className={`py-2 px-3 text-right font-mono ${b.debt_chain_break_count > 0 ? "text-[#D95D39] font-semibold" : "text-slate-400"}`}>{b.debt_chain_break_count > 0 ? b.debt_chain_break_count : "—"}</td>
                        <td className={`py-2 px-3 text-right font-mono ${(b.stock_chain_break_count || 0) > 0 ? "text-[#D95D39] font-semibold" : "text-slate-400"}`}>{(b.stock_chain_break_count || 0) > 0 ? b.stock_chain_break_count : "—"}</td>
                        <td className={`py-2 px-3 text-right font-mono font-bold ${clean ? "text-[#4A7C59]" : "text-[#D95D39]"}`}>{clean ? "✓" : b.total_anomalies}</td>
                        <td className="py-2 pl-3 text-right">
                          {!clean && (
                            <Link
                              to={`/daily-summary?branch_id=${b.branch_id}&month=${anomaliesMonth}`}
                              className="text-[11px] font-medium text-[#78350F] hover:underline inline-flex items-center gap-0.5"
                              data-testid={`anomaly-goto-${b.branch_id}`}
                            >
                              İncele <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {isManager && monthlyPerBranch.length > 0 && (
        <div className="k-card p-5">
          <h3 className="font-display font-medium text-slate-900 mb-1">Şubelere Göre Ciro</h3>
          <p className="text-xs text-slate-500 mb-4">Bulunduğumuz ay · Ay başından bugüne · Yüksekten düşüğe sıralı</p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyPerBranch.slice(0, 15)}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                <XAxis dataKey="name" tick={CHART_AXIS_TICK} />
                <YAxis tick={CHART_AXIS_TICK} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => fmtTL(v)} />
                <Bar dataKey="turnover" name="Ciro" fill="#D95D39" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {isManager && branchPerf?.branches && (
        <div className="k-card p-5" data-testid="kg-doner-cirosu-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-medium text-slate-900 flex items-center gap-2">
                <Scale className="h-4 w-4 text-[#4F46E5]" />
                kg Döner Cirosu — Şube Karşılaştırması (Bu Ay)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Formül: (Toplam Ciro − Çıtır Cirosu) ÷ Pişmiş Döner. Hedef: <b>2.100 TL/kg</b>
              </p>
            </div>
          </div>
          <div className="space-y-2 max-h-96 overflow-auto" data-testid="kg-doner-list">
            {(() => {
              const withData = (branchPerf.branches || [])
                .filter((b) => (b.kg_doner_cirosu || 0) > 0)
                .sort((a, b) => (b.kg_doner_cirosu || 0) - (a.kg_doner_cirosu || 0));
              if (withData.length === 0) {
                return (
                  <div className="text-sm text-slate-500 py-4 text-center">
                    Bu ay için ürün satış verisi olan şube bulunamadı — günlük raporlara ürün miktarları girildikçe burada listelenecek.
                  </div>
                );
              }
              return withData.map((b) => {
                const val = b.kg_doner_cirosu || 0;
                const target = 2100;
                const pct = Math.min(100, (val / target) * 100);
                const color = val >= 2000 ? "#4A7C59" : val >= 1700 ? "#B8451F" : "#C84B31";
                const bg = val >= 2000 ? "#EDF4F0" : val >= 1700 ? "#FEF5EC" : "#FDF0ED";
                return (
                  <div key={b.branch_id} className="p-3 rounded-lg border border-[#EAE6DF] bg-white" data-testid={`kg-doner-row-${b.branch_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{b.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          Pişmiş döner: {(b.pismis_doner_kg || 0).toLocaleString("tr-TR")} kg · Çıtır ciro: {fmtTL(b.citir_ciro || 0)}
                        </div>
                      </div>
                      <div
                        className="font-mono text-sm font-semibold whitespace-nowrap px-2 py-1 rounded-md"
                        style={{ backgroundColor: bg, color: color }}
                      >
                        {fmtTL(val)}<span className="text-[10px] font-normal opacity-70 ml-0.5">/kg</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#F3F1EC] overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Son Yaptığın Raporlar — şube + tarih filtreli */}
      <div className="k-card p-5" data-testid="recent-reports-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display font-medium text-slate-900 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-[#D95D39]" />
              Son Yaptığın Raporlar
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Son 20 rapor · şube ve tarih filtresi</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {isManager && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Şube</div>
                <Select value={recentBranch} onValueChange={setRecentBranch}>
                  <SelectTrigger data-testid="recent-branch-select" className="h-9 bg-white w-52">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Şubeler</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Başlangıç</div>
              <Input data-testid="recent-from-input" type="date" value={recentFrom}
                onChange={(e) => setRecentFrom(e.target.value)} className="h-9 bg-white w-36" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Bitiş</div>
              <Input data-testid="recent-to-input" type="date" value={recentTo}
                onChange={(e) => setRecentTo(e.target.value)} className="h-9 bg-white w-36" />
            </div>
            {(recentFrom || recentTo || recentBranch !== "all") && (
              <button
                type="button"
                onClick={() => { setRecentFrom(""); setRecentTo(""); setRecentBranch("all"); }}
                className="text-xs text-slate-500 hover:text-[#D95D39] underline underline-offset-2 h-9"
              >
                Filtreleri temizle
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F1EC] text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Tarih</th>
                {isManager && <th className="text-left px-3 py-2">Şube</th>}
                <th className="text-right px-3 py-2">Ciro</th>
                <th className="text-right px-3 py-2">Gider</th>
                <th className="text-left px-3 py-2 pl-3">Güncelleyen</th>
                <th className="text-right px-3 py-2 w-[110px]">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAE6DF]">
              {recent.length === 0 && (
                <tr><td colSpan={isManager ? 6 : 5} className="text-center py-6 text-slate-400">Bu filtreye uygun rapor bulunamadı.</td></tr>
              )}
              {recent.map((r) => {
                // Gider = Nakit Masraflar (total_expense) + İşçilik (personnel_cost).
                // Backend `total_expense` sadece nakit masrafları içerir; UI'daki Dashboard KPI'sı ile aynı formül.
                const giderToplam = (r?.totals?.total_expense || 0) + (r?.totals?.personnel_cost || 0);
                const editUrl = `/report/new?date=${r.date}&branch_id=${r.branch_id}`;
                return (
                  <tr key={r.id} data-testid={`recent-row-${r.id}`}
                      className="hover:bg-[#FAF8F4] cursor-pointer"
                      onClick={() => navigate(editUrl)}>
                    <td className="px-3 py-2 font-mono">{r.date}</td>
                    {isManager && <td className="px-3 py-2 text-slate-700">{r.branch_name || r.branch_id?.slice(0, 6)}</td>}
                    <td className="px-3 py-2 text-right tabular-nums">{fmtTL(r?.totals?.gercek_ciro ?? r?.totals?.turnover)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtTL(giderToplam)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.updated_by || "-"}</td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        <Link to={editUrl} data-testid={`recent-open-${r.id}`}
                              title="Düzenle"
                              className="p-1.5 rounded-md hover:bg-[#F3F1EC] text-slate-500 hover:text-slate-900">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <button type="button"
                                data-testid={`recent-dl-${r.id}`}
                                title="Excel indir"
                                onClick={() => downloadFile(`/export/report/${r.id}`, `kasa_${r.date}.xlsx`).catch(() => {})}
                                className="p-1.5 rounded-md hover:bg-[#F3F1EC] text-slate-500 hover:text-[#D95D39]">
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {deptDaily && (
        <div className="k-card p-5" data-testid="dept-breakdown-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-display font-medium text-slate-900 flex items-center gap-2">
                <PieChartIcon className="h-4 w-4 text-[#D95D39]" />
                Departmana Göre Ciro (Günlük)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Seçilen gün için kanal bazında ciro · indirim · sipariş · indirim %</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Tarih</span>
                <Input
                  data-testid="dept-date-input"
                  type="date"
                  value={deptDate}
                  onChange={(e) => setDeptDate(e.target.value)}
                  className="h-9 bg-white w-40"
                />
              </div>
              {isManager && (deptDaily.dept_by_branch?.length || 0) > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Görünüm</span>
                  <Select value={deptScope} onValueChange={setDeptScope}>
                    <SelectTrigger data-testid="dept-scope-select" className="h-9 bg-white w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Şubeler (Toplam)</SelectItem>
                      {deptDaily.dept_by_branch.map((b) => (
                        <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed" data-testid="dept-breakdown-table">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[16%]" />
                <col className="w-[15%]" />
                <col className="w-[12%]" />
                <col className="w-[13%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead className="bg-[#F3F1EC] text-[10px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Departman</th>
                  <th className="text-right px-3 py-2">Ciro</th>
                  <th className="text-right px-3 py-2">İndirim</th>
                  <th className="text-right px-3 py-2">Sipariş</th>
                  <th className="text-right px-3 py-2">İndirim %</th>
                  <th className="text-right px-3 py-2 bg-[#EDE6D5]">Ciro Payı %</th>
                </tr>
              </thead>
              <tbody>
                {deptRows.map((row) => {
                  const revNum = Number(row?.rev ?? 0);
                  const discNum = Number(row?.disc ?? 0);
                  const ordersNum = Number(row?.orders ?? 0);
                  const rateNum = Number(row?.rate ?? 0);
                  const totalRev = Number(deptTotals?.rev ?? 0);
                  const share = totalRev > 0 ? (revNum / totalRev) * 100 : 0;
                  return (
                    <tr key={row.key} className="border-t border-[#EAE6DF]" data-testid={`dept-row-${row.key}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        <div className="flex items-center gap-2">
                          <DeptIcon dept={row.key} />
                          <span>{row.label}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-900">{fmtTL(revNum)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#C24F2C]">{discNum > 0 ? fmtTL(discNum) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">{ordersNum.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#4A6FA5]">{discNum > 0 ? `%${rateNum.toFixed(2)}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600 bg-[#F8F5EE]">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                <tr className="bg-[#F3F1EC] font-bold border-t-2 border-[#EAE6DF]">
                  <td className="px-3 py-2 text-slate-900">Toplam</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900" data-testid="dept-total-rev">{fmtTL(Number(deptTotals?.rev ?? 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#C24F2C]" data-testid="dept-total-disc">{fmtTL(Number(deptTotals?.disc ?? 0))}</td>
                  <td className="px-3 py-2 text-right font-mono" data-testid="dept-total-orders">{Number(deptTotals?.orders ?? 0).toFixed(0)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#4A6FA5]" data-testid="dept-total-rate">{Number(deptTotals?.disc ?? 0) > 0 ? `%${Number(deptTotalRate ?? 0).toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono bg-[#EDE6D5]">100.0%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isManager && health.length > 0 && (
        <div className="k-card p-5" data-testid="health-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-medium text-slate-900 flex items-center gap-2">
                <HeartPulse className="h-4 w-4 text-[#4A7C59]" />
                Şube Sağlık Skoru (Son 30 Gün)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Porsiyon Verimliliği %40 · Zamanlılık %30 · Kar Marjı %30</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">🏆 En Sağlıklı 3 Şube</div>
              <div className="space-y-2" data-testid="top-branches">
                {health.slice(0, 3).map((b) => (
                  <HealthRow key={b.branch_id} b={b} tone="good" />
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">⚠️ Dikkat Gereken 3 Şube</div>
              <div className="space-y-2" data-testid="bottom-branches">
                {[...health].reverse().slice(0, 3).map((b) => (
                  <HealthRow key={b.branch_id} b={b} tone="bad" />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthRow({ b, tone }) {
  const score = Number(b?.score ?? 0);
  const portion = Number(b?.portion_score ?? 0);
  const timely = Number(b?.timely_score ?? 0);
  const profit = Number(b?.profit_score ?? 0);
  const scoreColor =
    score >= 80 ? "text-[#4A7C59] bg-[#EDF4F0]" :
    score >= 60 ? "text-[#F2A65A] bg-[#FEF5EC]" :
    "text-[#C84B31] bg-[#FDF0ED]";
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${tone === "good" ? "border-[#EAE6DF]" : "border-[#EDD9D4]"} bg-white`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 truncate">{b?.name || "-"}</div>
        <div className="flex gap-3 mt-1 text-[10px] font-mono text-slate-500">
          <span title="Porsiyon verimliliği (kg döner cirosu)">Porsiyon: {portion.toFixed(0)}</span>
          <span title="Zamanında rapor girme">Zaman: {timely.toFixed(0)}</span>
          <span title="Kar marjı">Kar: {profit.toFixed(0)}</span>
        </div>
      </div>
      <div className={`ml-3 font-mono text-2xl font-bold w-16 h-14 rounded-lg flex items-center justify-center ${scoreColor}`}>
        {score.toFixed(0)}
      </div>
    </div>
  );
}
